import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import Chip from '@mui/material/Chip';
import AccordionDetails from '@mui/material/AccordionDetails';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { SfLocationLabelText, SfAccordionSummary } from './Filter.style';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { RootState } from '../../store/store';
import { useSelector, useDispatch } from 'react-redux';
import {
  setDistance,
  setMaxDistance,
  setShowAnyLocation,
  setIsVirtual,
  setSelectedSite,
  clearSelectedSite,
} from 'src/features/coveoSlice';
import { debounce } from 'lib/utils/debounceFunc';
import ZipcodeFilter from './ZipCodeFilter';
import UiFormLabel from '../shared/FormLabel/FormLabel';
import {
  clearFindTrialsSiteParamsFromUrl,
  getFindTrialsSiteParams,
} from 'lib/utils/findTrialsSiteParam';

const DistanceFilter = ({ data }: any) => {
  const DEFAULT_VALUE = data?.fields?.defaultDistance?.value || 50;
  const dispatch = useDispatch();
  const { distance, zipCode, showAnyLocation } = useSelector(
    (state: RootState) => state.coveoResult
  );

  const isDisable = !zipCode || zipCode.length < 5 || showAnyLocation;

  const onChange = (e: any) => {
    dispatch(setDistance(+e.target.value));
  };

  const debouncedOnChange = debounce(onChange, 1000);

  useEffect(() => {
    dispatch(setDistance(DEFAULT_VALUE));
    dispatch(setMaxDistance(data?.fields?.maxDistance?.value));
  }, [data?.fields?.maxDistance?.value, data?.fields?.minDistance?.value, dispatch]);

  return (
    <>
      <UiFormLabel>
        <Text field={data?.fields?.label}></Text>
      </UiFormLabel>
      <Slider
        defaultValue={DEFAULT_VALUE}
        aria-label="Default"
        valueLabelDisplay="auto"
        min={data?.fields?.minDistance?.value}
        max={data?.fields?.maxDistance?.value}
        onChange={debouncedOnChange}
        disabled={isDisable}
      />
      <UiFormLabel>
        {distance} {data?.fields?.milesText?.value} / {Math.round(distance * 1.60934)}{' '}
        {data?.fields?.kmText?.value}{' '}
      </UiFormLabel>
    </>
  );
};

const ShowAnyLocationFilter = ({ data }: any) => {
  const dispatch = useDispatch();
  const value = useSelector((state: RootState) => state.coveoResult.showAnyLocation);

  const toogleShowAnyLocation = () => {
    dispatch(setShowAnyLocation());
  };

  return (
    <>
      <FormControlLabel
        control={<Checkbox checked={value} onChange={toogleShowAnyLocation} name={data?.key} />}
        label={
          <UiFormLabel>
            <Text field={data?.fields?.label}></Text>
          </UiFormLabel>
        }
      />
    </>
  );
};

const ShowVirtualTrialFilter = ({ data }: any) => {
  const dispatch = useDispatch();
  const value = useSelector((state: RootState) => state.coveoResult.isVirtual);

  const toogleIsVirtual = () => {
    dispatch(setIsVirtual());
  };

  return (
    <>
      <FormControlLabel
        control={<Checkbox checked={value} onChange={toogleIsVirtual} name={data?.key} />}
        label={
          <UiFormLabel>
            <Text field={data?.fields?.label}></Text>
          </UiFormLabel>
        }
      />
    </>
  );
};

const LocationFilter = (props: any): JSX.Element => {
  const dispatch = useDispatch();
  const { asPath } = useRouter();
  const siteLockPath = asPath.split('#')[0];
  const { selectedSiteId, selectedSiteName, showAnyLocation } = useSelector(
    (state: RootState) => state.coveoResult
  );
  const { heading } = props.fields;
  const LOCATION_FIELDS = {
    ZIPCODE: 'Zipcode',
    DISTANCE: 'Distance',
    VIRTUALTRIAL: 'virtualTrial',
    SHOW_ANY_LOCATION: 'showAnyLocation',
  };
  const zipcodeRegex = props?.zipcodeRegex;
  const zipcodeRegexFailedErrorMessage = props?.zipcodeRegexFailedErrorMessage;
  const zipcodeRequiredErrorMessage = props?.zipcodeRequiredErrorMessage;
  const isZipcodeRequired = props?.isZipcodeRequired;
  const locationFilterData: any = {};
  props?.fields?.data?.forEach((item: any) => {
    locationFilterData[item?.fields?.key?.value] = {
      key: item?.fields?.key?.value,
      fields: item?.fields,
    };
  });

  useEffect(() => {
    const { siteId, siteName } = getFindTrialsSiteParams();
    if (!siteId) {
      dispatch(clearSelectedSite());
      return;
    }

    dispatch(setSelectedSite({ siteId, siteName: siteName || '' }));

    // Site lock should always use "Show Any Location".
    if (!showAnyLocation) {
      dispatch(setShowAnyLocation());
    }
  }, [dispatch, showAnyLocation, siteLockPath]);

  const onClearSite = () => {
    dispatch(clearSelectedSite());
    clearFindTrialsSiteParamsFromUrl();
  };

  return (
    <Grid item sx={{ marginTop: '1rem' }}>
      <Accordion disableGutters defaultExpanded>
        <SfAccordionSummary
          expandIcon={
            <ChevronRightIcon
              sx={{
                color: (theme) => theme.palette.neutral8.main,
                height: '32px',
                width: '32px',
              }}
            />
          }
          aria-controls="location-content"
          id="location-header"
        >
          <SfLocationLabelText>
            {' '}
            <Text field={heading}></Text>
          </SfLocationLabelText>
        </SfAccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {selectedSiteId ? (
            <Chip
              label={selectedSiteName || selectedSiteId}
              onDelete={onClearSite}
              deleteIcon={<CancelRoundedIcon />}
              sx={{
                alignSelf: 'flex-start',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                height: '40px',
                backgroundColor: '#EEF0F2',
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: '320px',
                padding: '4px',
                '& .MuiChip-label': {
                  padding: '0 8px',
                  fontFamily: (theme) => `${theme.palette.primaryFontFamilyType}, sans-serif`,
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '21px',
                  letterSpacing: '0.28px',
                  color: (theme) => theme.palette.primary.main,
                },
                '& .MuiChip-deleteIcon': {
                  color: (theme) => `${theme.palette.primary.main} !important`,
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  margin: '0 2px 0 0',
                },
              }}
            />
          ) : null}
          <ZipcodeFilter
            data={locationFilterData[LOCATION_FIELDS.ZIPCODE]}
            zipcodeRegex={zipcodeRegex}
            zipcodeRegexFailedErrorMessage={zipcodeRegexFailedErrorMessage}
            zipcodeRequiredErrorMessage={zipcodeRequiredErrorMessage}
            isZipcodeRequired={isZipcodeRequired}
          />
          <DistanceFilter data={locationFilterData[LOCATION_FIELDS.DISTANCE]} />
          <ShowAnyLocationFilter data={locationFilterData[LOCATION_FIELDS.SHOW_ANY_LOCATION]} />
          <ShowVirtualTrialFilter data={locationFilterData[LOCATION_FIELDS.VIRTUALTRIAL]} />
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default LocationFilter;
